// src/components/Header.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  FiUser,
  FiLogOut,
  FiSettings,
  FiLock,
  FiChevronDown,
  FiHelpCircle,
  FiSun,
  FiMoon,
  FiShoppingBag,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, Alert, IconButton,
  InputAdornment, CircularProgress, Box, LinearProgress,
  Stack, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ACCE_BRAND } from '../../theme/colors';

// ────────────────────────────────────────
// Role → friendly label
// ────────────────────────────────────────
const ROLE_LABELS = {
  admin:                           'Administrator',
  manager:                         'F&B Manager',
  Lounge_Managers:                 'Lounge Manager',
  Cosmetics_Beauty_Store_Manager:  'Cosmetics & Beauty Store Manager',
  Cosmetics_Beauty_Storer_Cashier: 'Cosmetics & Beauty Store Cashier',
  Fashion_LifeStyle_Manager:       'Fashion & LifeStyle Manager',
  Fashion_LifeStyle_Cashier:       'Fashion & LifeStyle Cashier',
  cashier:                         'Cashier',
  waiter:                          'Waiter',
  account:                         'Accountant',
  ict:                             'ICT Officer',
  kitchen:                         'Kitchen Staff',
  staff:                           'Staff',
  store_keeper:                    'Store Keeper',
  auditor:                         'Auditor',
  hr:                              'HR Personnel',
  tuckshop_attendant:              'Tuckshop Attendant',
};

// ────────────────────────────────────────
// API CLIENT
// ────────────────────────────────────────
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const extractError = (err) => {
  const d = err?.response?.data;
  if (!d) return err.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (d.errors) {
    const first = Object.values(d.errors)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return d.message || 'Request failed';
};

/**
 * Change password — sends BOTH naming conventions so the backend
 * accepts whichever one its validator expects.
 */
const apiChangePassword = async ({ currentPassword, newPassword, confirmPassword }) => {
  const payload = {
    // Primary (Laravel convention)
    current_password: currentPassword,
    password: newPassword,
    password_confirmation: confirmPassword,

    // Aliases for backends using a `new_password` naming scheme
    new_password: newPassword,
    new_password_confirmation: confirmPassword,
  };

  try {
    const { data } = await api.post('/auth/change-password', payload);
    return { success: true, ...data };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
};

// ────────────────────────────────────────
// PASSWORD STRENGTH
// ────────────────────────────────────────
const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: 'Empty', color: '#94a3b8' };

  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  const map = {
    0: { label: 'Very weak', color: '#dc2626' },
    1: { label: 'Weak',      color: '#dc2626' },
    2: { label: 'Fair',      color: '#f59e0b' },
    3: { label: 'Good',      color: '#eab308' },
    4: { label: 'Strong',    color: '#16a34a' },
    5: { label: 'Very strong', color: '#16a34a' },
  };

  return { score, ...map[score] };
};

// ════════════════════════════════════════════
// FIELD LABEL — sits above the input, never clipped
// ════════════════════════════════════════════
const FieldLabel = ({ children, required }) => (
  <Typography
    component="label"
    variant="caption"
    sx={{
      display: 'block',
      mb: 0.75,
      fontWeight: 700,
      color: '#334155',
      fontSize: '0.72rem',
      letterSpacing: 0.3,
      fontFamily: '"Inter", sans-serif',
    }}
  >
    {children}
    {required && (
      <Box component="span" sx={{ color: '#dc2626', ml: 0.3 }}>*</Box>
    )}
  </Typography>
);

// ════════════════════════════════════════════
// PASSWORD FIELD STYLES
// ════════════════════════════════════════════
const passwordFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fff',
    transition: 'all 0.15s ease',
    '& fieldset': {
      borderColor: alpha(ACCE_BRAND.navy, 0.15),
    },
    '&:hover fieldset': {
      borderColor: alpha(ACCE_BRAND.orange, 0.5),
    },
    '&.Mui-focused fieldset': {
      borderColor: ACCE_BRAND.orange,
      borderWidth: 2,
    },
    '& input': {
      py: 1.6,
      fontSize: '0.95rem',
    },
  },
  '& .MuiFormHelperText-root': {
    ml: 0.5,
    mt: 0.5,
    fontSize: '0.72rem',
    minHeight: '1em',
    fontFamily: '"Inter", sans-serif',
  },
};

// ════════════════════════════════════════════
// INLINE CHANGE-PASSWORD DIALOG
// ════════════════════════════════════════════
const ChangePasswordDialog = ({ open, onClose, onSuccess }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Reset every time the dialog opens
  useEffect(() => {
    if (open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      setSubmitting(false);
      setServerError('');
      setFieldErrors({});
    }
  }, [open]);

  const strength = getPasswordStrength(newPassword);

  const validate = useCallback(() => {
    const errs = {};

    if (!currentPassword) errs.current_password = 'Current password is required';

    if (!newPassword) {
      errs.password = 'New password is required';
    } else if (newPassword.length < 8) {
      errs.password = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(newPassword)) {
      errs.password = 'Password must contain an uppercase letter';
    } else if (!/[a-z]/.test(newPassword)) {
      errs.password = 'Password must contain a lowercase letter';
    } else if (!/\d/.test(newPassword)) {
      errs.password = 'Password must contain a digit';
    }

    if (newPassword && currentPassword && newPassword === currentPassword) {
      errs.password = 'New password must be different from current';
    }

    if (newPassword !== confirmPassword) {
      errs.password_confirmation = 'Passwords do not match';
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }, [currentPassword, newPassword, confirmPassword]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    setSubmitting(true);
    setServerError('');
    setFieldErrors({});

    const res = await apiChangePassword({
      currentPassword,
      newPassword,
      confirmPassword,
    });

    setSubmitting(false);

    if (!res.success) {
      setServerError(res.error || 'Failed to change password.');

      // Route the error to the right field
      const err = String(res.error || '').toLowerCase();
      if (err.includes('current password') || err.includes('current_password')) {
        setFieldErrors((prev) => ({ ...prev, current_password: res.error }));
      } else if (err.includes('new password') || err.includes('password is required')) {
        setFieldErrors((prev) => ({ ...prev, password: res.error }));
      } else if (err.includes('confirmation') || err.includes('do not match')) {
        setFieldErrors((prev) => ({ ...prev, password_confirmation: res.error }));
      }
      return;
    }

    if (onSuccess) onSuccess(res);
    onClose?.();
  }, [validate, currentPassword, newPassword, confirmPassword, onSuccess, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !submitting && newPassword && confirmPassword && currentPassword) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1.5,
          bgcolor: alpha(ACCE_BRAND.orange, 0.05),
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40, height: 40,
              borderRadius: '10px',
              bgcolor: alpha(ACCE_BRAND.orange, 0.15),
              color: ACCE_BRAND.orange,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <FiLock size={18} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
              Change Password
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Inter", sans-serif' }}>
              Secure your account with a strong password
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={submitting} size="small">
          <Typography sx={{ fontSize: 20, lineHeight: 1 }}>×</Typography>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {serverError && (
          <Alert
            severity="error"
            onClose={() => setServerError('')}
            sx={{ mb: 2.5, borderRadius: 2 }}
          >
            {serverError}
          </Alert>
        )}

        <Stack spacing={2.5}>
          {/* ── Current password ── */}
          <Box>
            <FieldLabel required>Current Password</FieldLabel>
            <TextField
              autoFocus
              fullWidth
              type={showCurrent ? 'text' : 'password'}
              placeholder="Enter your current password"
              variant="outlined"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                if (fieldErrors.current_password) {
                  setFieldErrors((p) => ({ ...p, current_password: '' }));
                }
              }}
              onKeyDown={handleKeyDown}
              error={!!fieldErrors.current_password}
              helperText={fieldErrors.current_password || ' '}
              disabled={submitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiLock size={16} color="#94a3b8" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowCurrent((s) => !s)}
                      edge="end"
                      tabIndex={-1}
                    >
                      <Typography sx={{ fontSize: 14, color: '#94a3b8' }}>
                        {showCurrent ? '🙈' : '👁'}
                      </Typography>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={passwordFieldSx}
            />
          </Box>

          {/* ── New password ── */}
          <Box>
            <FieldLabel required>New Password</FieldLabel>
            <TextField
              fullWidth
              type={showNew ? 'text' : 'password'}
              placeholder="At least 8 characters"
              variant="outlined"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (fieldErrors.password) setFieldErrors((p) => ({ ...p, password: '' }));
              }}
              onKeyDown={handleKeyDown}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password || ' '}
              disabled={submitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiLock size={16} color="#94a3b8" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowNew((s) => !s)}
                      edge="end"
                      tabIndex={-1}
                    >
                      <Typography sx={{ fontSize: 14, color: '#94a3b8' }}>
                        {showNew ? '🙈' : '👁'}
                      </Typography>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={passwordFieldSx}
            />

            {newPassword && (
              <Box sx={{ mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: strength.color, fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                    {strength.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Inter", sans-serif' }}>
                    {newPassword.length} character{newPassword.length === 1 ? '' : 's'}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(strength.score / 5) * 100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: alpha('#000', 0.06),
                    '& .MuiLinearProgress-bar': {
                      bgcolor: strength.color,
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* ── Confirm new password ── */}
          <Box>
            <FieldLabel required>Confirm New Password</FieldLabel>
            <TextField
              fullWidth
              type={showConfirm ? 'text' : 'password'}
              placeholder="Re-enter the new password"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (fieldErrors.password_confirmation) {
                  setFieldErrors((p) => ({ ...p, password_confirmation: '' }));
                }
              }}
              onKeyDown={handleKeyDown}
              error={!!fieldErrors.password_confirmation}
              helperText={fieldErrors.password_confirmation || ' '}
              disabled={submitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiLock size={16} color="#94a3b8" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowConfirm((s) => !s)}
                      edge="end"
                      tabIndex={-1}
                    >
                      <Typography sx={{ fontSize: 14, color: '#94a3b8' }}>
                        {showConfirm ? '🙈' : '👁'}
                      </Typography>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={passwordFieldSx}
            />
          </Box>

          <Divider sx={{ my: 0.5 }} />

          {/* ── Requirements card ── */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(ACCE_BRAND.navy, 0.03),
              border: `1px solid ${alpha(ACCE_BRAND.navy, 0.1)}`,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={800}
              sx={{
                display: 'block',
                mb: 1,
                color: ACCE_BRAND.navy,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              Password Requirements
            </Typography>
            <Stack spacing={0.75}>
              {[
                { label: 'At least 8 characters', ok: newPassword.length >= 8 },
                { label: 'One uppercase letter',  ok: /[A-Z]/.test(newPassword) },
                { label: 'One lowercase letter',  ok: /[a-z]/.test(newPassword) },
                { label: 'One number',            ok: /\d/.test(newPassword) },
              ].map(({ label, ok }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 16, height: 16, borderRadius: '50%',
                      bgcolor: ok ? alpha('#16a34a', 0.15) : alpha('#94a3b8', 0.15),
                      color: ok ? '#16a34a' : '#94a3b8',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: 10, fontWeight: 900,
                    }}
                  >
                    {ok ? '✓' : '•'}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: ok ? '#16a34a' : 'text.secondary',
                      fontWeight: ok ? 700 : 500,
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
            fontFamily: '"Inter", sans-serif',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            submitting
            || !currentPassword
            || !newPassword
            || !confirmPassword
          }
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{
            bgcolor: ACCE_BRAND.orange,
            '&:hover': { bgcolor: ACCE_BRAND.orangeDark },
            '&.Mui-disabled': { bgcolor: alpha(ACCE_BRAND.orange, 0.4), color: '#fff' },
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: 2,
            px: 3,
            fontFamily: '"Inter", sans-serif',
          }}
        >
          {submitting ? 'Updating…' : 'Update Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// HEADER
// ════════════════════════════════════════════
const Header = () => {
  const navigate = useNavigate();

  const [currentTime, setCurrentTime]         = useState(new Date());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSettings, setShowSettings]       = useState(false);
  const [isDarkMode, setIsDarkMode]           = useState(false);
  const [changePwOpen, setChangePwOpen]       = useState(false);

  const profileRef = useRef(null);

  // ── Logged-in user ──
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem('user_data');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const fullName = user
    ? `${user.first_name || ''} ${user.other_names || ''}`.trim()
    : 'Guest';

  const initials = user
    ? `${(user.first_name || '?')[0]}${(user.other_names || '')[0] || ''}`.toUpperCase()
    : '?';

  const roleLabel = ROLE_LABELS[user?.role] || user?.role || 'User';

  // Digital clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  // Close profile dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // ── Logout ──
  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to logout?')) return;

    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await fetch(`${FULL_API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch {
      // ignore — clear locally regardless
    }

    localStorage.clear();
    navigate('/');
  };

  // ── Password change ──
  const handleChangePassword = () => {
    setShowProfileMenu(false);
    setChangePwOpen(true);
  };

  const handleProfileSettings = () => {
    navigate('/profile');
    setShowProfileMenu(false);
  };

  return (
    <>
      <header className="acce-header">
        {/* ── Left: brand + title ── */}
        <div className="acce-header-left">
          <div className="acce-header-brand">
            <div className="acce-header-brand-icon">
              <FiShoppingBag size={18} />
            </div>
            <div className="acce-header-brand-text">
              <span className="acce-header-brand-name">{ACCE_BRAND.name || 'ACCE'}</span>
              <span className="acce-header-brand-sub">Point of Sale</span>
            </div>
          </div>
        </div>

        {/* ── Center: clock ── */}
        <div className="acce-header-center">
          <div className="acce-clock">
            <span className="acce-clock-dot" />
            <span className="acce-clock-time">{formatTime(currentTime)}</span>
            <span className="acce-clock-divider" />
            <span className="acce-clock-date">{formatDate(currentTime)}</span>
          </div>
        </div>

        {/* ── Right: profile ── */}
        <div className="acce-header-right">
          <div className="acce-profile-wrap" ref={profileRef}>
            <button
              type="button"
              className="acce-profile"
              onClick={() => setShowProfileMenu((v) => !v)}
            >
              <div className="acce-avatar">{initials}</div>
              <div className="acce-profile-text">
                <span className="acce-profile-name">{fullName}</span>
                <span className="acce-profile-role">{roleLabel}</span>
              </div>
              <FiChevronDown
                size={14}
                className={`acce-chevron ${showProfileMenu ? 'is-open' : ''}`}
              />
            </button>

            {showProfileMenu && (
              <div className="acce-dropdown acce-dropdown-profile">
                <div className="acce-profile-card">
                  <div className="acce-avatar acce-avatar-lg">{initials}</div>
                  <div className="acce-profile-card-text">
                    <div className="acce-profile-card-name">{fullName}</div>
                    <div className="acce-profile-card-email">{user?.email || 'not signed in'}</div>
                  </div>
                </div>

                <div className="acce-menu">
                  <button type="button" className="acce-menu-item" onClick={handleChangePassword}>
                    <FiLock size={16} />
                    <span>Change Password</span>
                  </button>

                  {showSettings && (
                    <div className="acce-submenu">
                      <button
                        type="button"
                        className="acce-menu-item acce-menu-item-sub"
                        onClick={() => setIsDarkMode((v) => !v)}
                      >
                        {isDarkMode ? <FiSun size={14} /> : <FiMoon size={14} />}
                        <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                      </button>
                      <button
                        type="button"
                        className="acce-menu-item acce-menu-item-sub"
                        onClick={() => window.open('/support', '_blank')}
                      >
                        <FiHelpCircle size={14} />
                        <span>Help & Support</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="acce-menu-footer">
                  <button type="button" className="acce-menu-item acce-menu-danger" onClick={handleLogout}>
                    <FiLogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Inline styles ── */}
        <style>{`
          .acce-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            padding: 10px 28px;
            background: linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%);
            border-bottom: 1px solid rgba(42, 58, 84, 0.08);
            box-shadow: 0 1px 2px rgba(42, 58, 84, 0.03);
            position: sticky;
            top: 0;
            z-index: 100;
            min-height: 68px;
          }

          /* ── Left: brand ── */
          .acce-header-left {
            display: flex;
            align-items: center;
            min-width: 0;
          }

          .acce-header-brand {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .acce-header-brand-icon {
            width: 38px;
            height: 38px;
            border-radius: 11px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            background: linear-gradient(135deg, #D96A0A 0%, #F3811E 55%, #FFA04D 100%);
            box-shadow: 0 4px 14px rgba(243, 129, 30, 0.35), inset 0 1px 0 rgba(255,255,255,0.25);
            flex-shrink: 0;
          }

          .acce-header-brand-text {
            display: flex;
            flex-direction: column;
            line-height: 1.1;
          }

          .acce-header-brand-name {
            font-size: 15px;
            font-weight: 800;
            color: ${ACCE_BRAND.navy};
            letter-spacing: 0.2px;
          }

          .acce-header-brand-sub {
            font-size: 10.5px;
            font-weight: 600;
            color: ${ACCE_BRAND.mutedText};
            letter-spacing: 0.6px;
            text-transform: uppercase;
            margin-top: 1px;
          }

          /* ── Center: clock ── */
          .acce-header-center {
            display: flex;
            align-items: center;
            flex: 1;
            justify-content: center;
          }

          .acce-clock {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 7px 16px;
            border-radius: 999px;
            background: rgba(243, 129, 30, 0.06);
            border: 1px solid rgba(243, 129, 30, 0.12);
            color: ${ACCE_BRAND.navy};
          }

          .acce-clock-dot {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: #22c55e;
            box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
            animation: acce-pulse 2s ease-in-out infinite;
          }

          @keyframes acce-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.55; }
          }

          .acce-clock-time {
            font-family: 'SF Mono', 'Courier New', monospace;
            font-size: 14.5px;
            font-weight: 700;
            color: ${ACCE_BRAND.darkText};
            letter-spacing: 0.4px;
          }

          .acce-clock-divider {
            width: 1px;
            height: 14px;
            background: rgba(42, 58, 84, 0.15);
          }

          .acce-clock-date {
            font-size: 11.5px;
            font-weight: 600;
            color: ${ACCE_BRAND.mutedText};
            letter-spacing: 0.2px;
          }

          /* ── Right cluster ── */
          .acce-header-right {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          /* ── Profile button ── */
          .acce-profile-wrap { position: relative; }

          .acce-profile {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 4px 10px 4px 4px;
            border-radius: 12px;
            border: 1px solid rgba(42, 58, 84, 0.08);
            background: #fff;
            cursor: pointer;
            transition: all 0.2s ease;
            font-family: inherit;
          }

          .acce-profile:hover {
            border-color: rgba(243, 129, 30, 0.4);
            box-shadow: 0 4px 14px rgba(243, 129, 30, 0.12);
          }

          .acce-avatar {
            width: 34px;
            height: 34px;
            border-radius: 10px;
            background: linear-gradient(135deg, ${ACCE_BRAND.navyDark} 0%, ${ACCE_BRAND.navy} 60%, ${ACCE_BRAND.navyLight} 100%);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 800;
            font-size: 13px;
            letter-spacing: 0.5px;
            flex-shrink: 0;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.15);
          }

          .acce-avatar-lg {
            width: 44px;
            height: 44px;
            border-radius: 12px;
            font-size: 15px;
          }

          .acce-profile-text {
            display: flex;
            flex-direction: column;
            line-height: 1.2;
            text-align: left;
          }

          .acce-profile-name {
            font-size: 13px;
            font-weight: 700;
            color: ${ACCE_BRAND.darkText};
          }

          .acce-profile-role {
            font-size: 10.5px;
            font-weight: 600;
            color: ${ACCE_BRAND.mutedText};
            text-transform: uppercase;
            letter-spacing: 0.4px;
          }

          .acce-chevron {
            color: ${ACCE_BRAND.mutedText};
            transition: transform 0.2s ease;
          }
          .acce-chevron.is-open { transform: rotate(180deg); }
          .acce-chevron-sm { margin-left: auto; transition: transform 0.2s ease; }
          .acce-chevron-sm.is-open { transform: rotate(180deg); }

          /* ── Dropdown ── */
          .acce-dropdown {
            position: absolute;
            top: calc(100% + 12px);
            right: 0;
            background: #fff;
            border-radius: 14px;
            box-shadow: 0 20px 50px -10px rgba(42, 58, 84, 0.22), 0 0 0 1px rgba(42, 58, 84, 0.06);
            z-index: 1000;
            overflow: hidden;
            animation: acce-fade-in 0.16s ease;
          }

          @keyframes acce-fade-in {
            from { opacity: 0; transform: translateY(-6px); }
            to   { opacity: 1; transform: translateY(0); }
          }

          .acce-dropdown-profile { width: 250px; }

          /* ── Profile card ── */
          .acce-profile-card {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 16px;
            background: linear-gradient(135deg, rgba(42, 58, 84, 0.03) 0%, rgba(243, 129, 30, 0.04) 100%);
            border-bottom: 1px solid rgba(42, 58, 84, 0.06);
          }

          .acce-profile-card-text { min-width: 0; }

          .acce-profile-card-name {
            font-size: 13.5px;
            font-weight: 800;
            color: ${ACCE_BRAND.darkText};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .acce-profile-card-email {
            font-size: 11.5px;
            color: ${ACCE_BRAND.mutedText};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            margin-top: 2px;
          }

          /* ── Menu ── */
          .acce-menu { padding: 6px; }

          .acce-menu-item {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 11px;
            padding: 9px 10px;
            border-radius: 9px;
            background: none;
            border: none;
            cursor: pointer;
            font-family: inherit;
            font-size: 12.5px;
            font-weight: 600;
            color: ${ACCE_BRAND.bodyText};
            transition: all 0.15s ease;
            text-align: left;
          }

          .acce-menu-item:hover {
            background: ${ACCE_BRAND.grayLight};
            color: ${ACCE_BRAND.navy};
          }

          .acce-menu-item svg { color: ${ACCE_BRAND.mutedText}; flex-shrink: 0; }

          .acce-menu-item:hover svg { color: ${ACCE_BRAND.orange}; }

          .acce-menu-item-sub {
            padding-left: 26px;
            font-size: 12px;
            font-weight: 500;
          }

          .acce-submenu {
            margin: 2px 0;
            padding: 2px 0;
            border-left: 2px solid ${ACCE_BRAND.grayLight};
            margin-left: 18px;
          }

          .acce-menu-footer {
            padding: 6px;
            border-top: 1px solid rgba(42, 58, 84, 0.06);
          }

          .acce-menu-danger { color: #dc2626 !important; }
          .acce-menu-danger svg { color: #dc2626 !important; }
          .acce-menu-danger:hover {
            background: rgba(220, 38, 38, 0.08) !important;
          }

          /* ── Responsive ── */
          @media (max-width: 900px) {
            .acce-header { padding: 10px 16px; }
            .acce-header-brand-text { display: none; }
          }

          @media (max-width: 720px) {
            .acce-header-center { display: none; }
            .acce-profile-text { display: none; }
            .acce-profile { padding: 4px; }
            .acce-chevron { display: none; }
          }
        `}</style>
      </header>

      {/* ── Inline password change dialog ── */}
      <ChangePasswordDialog
        open={changePwOpen}
        onClose={() => setChangePwOpen(false)}
        onSuccess={() => {
          // Password updated — user stays logged in
        }}
      />
    </>
  );
};

export default Header;